import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useLocation } from "react-router-dom";

const CuerpoContext = createContext(null);
const DEFAULT_SLUG = "huasco";
const STORAGE_KEY = "superadmin_cuerpo_slug";
const CACHE_PREFIX = "bomberos_en_servicio_cuerpo_";

function slugDesdeRuta(pathname) {
  const ruta = String(pathname || "/");
  const partes = ruta.split("/").filter(Boolean);
  if (partes.length === 0) return null;

  const reservadas = new Set(["cia", "global", "partes", "admin", "admin-login"]);
  if (partes[0] === "admin" || partes[0] === "admin-login") {
    return sessionStorage.getItem(STORAGE_KEY) || DEFAULT_SLUG;
  }
  if (partes.length > 0 && !reservadas.has(partes[0])) return partes[0].toLowerCase();
  return DEFAULT_SLUG;
}

function serializarCuerpo(c) {
  if (!c) return null;
  return {
    id: c.id,
    nombre: c.nombre || "",
    slug: c.slug || "",
    activo: c.activo !== false,
    comuna: c.comuna || "",
    region: c.region || "",
    orden: c.orden ?? 999999,
    logoURL: c.logoURL || c.logo_url || c.logoUrl || c.logo || "",
    mensaje_desactivado: c.mensaje_desactivado || c.mensajeDesactivado || "",
  };
}

function cacheKey(slug) {
  return `${CACHE_PREFIX}${String(slug || "").toLowerCase()}`;
}

function leerCacheCuerpo(slug) {
  try {
    const raw = localStorage.getItem(cacheKey(slug));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function guardarCacheCuerpo(cuerpo) {
  try {
    if (cuerpo?.slug) localStorage.setItem(cacheKey(cuerpo.slug), JSON.stringify(serializarCuerpo(cuerpo)));
  } catch (_) {}
}

function borrarCacheCuerpo(slug) {
  try { localStorage.removeItem(cacheKey(slug)); } catch (_) {}
}

async function buscarCuerpoPorSlug(slug) {
  const q = query(collection(db, "cuerpos_bomberos"), where("slug", "==", slug), limit(1));
  const snap = await getDocs(q);
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data(), slug: d.data()?.slug || slug };
  }

  const porId = await getDoc(doc(db, "cuerpos_bomberos", slug));
  if (porId.exists()) return { id: porId.id, ...porId.data(), slug: porId.data()?.slug || slug };
  return null;
}

export function CuerpoProvider({ children }) {
  const location = useLocation();
  const [cuerpo, setCuerpo] = useState(null);
  const [cargandoCuerpo, setCargandoCuerpo] = useState(true);
  const [errorCuerpo, setErrorCuerpo] = useState(null);
  const [cuerpoNoEncontrado, setCuerpoNoEncontrado] = useState(false);

  const aplicarCuerpo = useCallback((nuevo, recordar = false) => {
    setCuerpo(nuevo);
    setErrorCuerpo(null);
    setCuerpoNoEncontrado(false);
    guardarCacheCuerpo(nuevo);
    if (recordar && nuevo?.slug && typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, nuevo.slug);
    }
  }, []);

  const seleccionarCuerpoPorSlug = useCallback(async (slug, recordar = true) => {
    if (!slug) return false;
    const normalizado = String(slug).toLowerCase();
    const cache = leerCacheCuerpo(normalizado);
    if (cache) aplicarCuerpo(cache, recordar);
    setCargandoCuerpo(!cache);
    try {
      const encontrado = await buscarCuerpoPorSlug(normalizado);
      if (!encontrado) throw new Error(`No existe un Cuerpo con slug ${slug}`);
      aplicarCuerpo(encontrado, recordar);
      return true;
    } catch (error) {
      console.error("Error seleccionando Cuerpo por slug:", error);
      borrarCacheCuerpo(normalizado);
      setCuerpo(null);
      setErrorCuerpo(error);
      setCuerpoNoEncontrado(true);
      return false;
    } finally {
      setCargandoCuerpo(false);
    }
  }, [aplicarCuerpo]);

  const seleccionarCuerpoPorId = useCallback(async (id, recordar = true) => {
    if (!id) return false;
    setCargandoCuerpo(true);
    try {
      const snap = await getDoc(doc(db, "cuerpos_bomberos", id));
      if (!snap.exists()) throw new Error(`No existe el Cuerpo ${id}`);
      const encontrado = { id: snap.id, ...snap.data() };
      aplicarCuerpo(encontrado, recordar);
      return true;
    } catch (error) {
      console.error("Error seleccionando Cuerpo por id:", error);
      setErrorCuerpo(error);
      setCuerpoNoEncontrado(true);
      return false;
    } finally {
      setCargandoCuerpo(false);
    }
  }, [aplicarCuerpo]);

  useEffect(() => {
    let activo = true;

    async function resolverCuerpo() {
      const slug = slugDesdeRuta(location.pathname);
      setErrorCuerpo(null);

      if (!slug) {
        if (activo) {
          setCuerpo(null);
          setCuerpoNoEncontrado(false);
          setCargandoCuerpo(false);
        }
        return;
      }

      const cache = leerCacheCuerpo(slug);
      if (cache && activo) {
        setCuerpo(cache);
        setCuerpoNoEncontrado(false);
        setCargandoCuerpo(false);
      } else if (activo) {
        setCargandoCuerpo(true);
      }

      try {
        const encontrado = await buscarCuerpoPorSlug(slug);
        if (encontrado) {
          if (activo) aplicarCuerpo(encontrado, false);
          return;
        }
        borrarCacheCuerpo(slug);
        if (activo) {
          setCuerpo(null);
          setCuerpoNoEncontrado(true);
        }
      } catch (error) {
        console.error("Error resolviendo cuerpo de bomberos:", error);
        if (activo && !cache) {
          setErrorCuerpo(error);
          setCuerpo(null);
          setCuerpoNoEncontrado(true);
        }
      } finally {
        if (activo) setCargandoCuerpo(false);
      }
    }

    resolverCuerpo();
    return () => { activo = false; };
  }, [aplicarCuerpo, location.pathname]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!cuerpo) {
      if (typeof window !== "undefined" && window.location.pathname === "/") {
        document.title = "Bomberos en Servicio | Digital Evolution BM";
      }
      return;
    }
    const etiqueta = String(cuerpo.comuna || cuerpo.nombre || cuerpo.id || "").trim();
    document.title = etiqueta ? `Bomberos en Servicio ${etiqueta}` : "Bomberos en Servicio";
  }, [cuerpo]);

  const value = useMemo(() => ({
    cuerpo,
    cuerpoId: cuerpo?.id || null,
    cuerpoSlug: cuerpo?.slug || DEFAULT_SLUG,
    cargandoCuerpo,
    errorCuerpo,
    cuerpoNoEncontrado,
    seleccionarCuerpoPorId,
    seleccionarCuerpoPorSlug,
  }), [cuerpo, cargandoCuerpo, errorCuerpo, cuerpoNoEncontrado, seleccionarCuerpoPorId, seleccionarCuerpoPorSlug]);

  return <CuerpoContext.Provider value={value}>{children}</CuerpoContext.Provider>;
}

export function useCuerpo() {
  const context = useContext(CuerpoContext);
  if (!context) throw new Error("useCuerpo debe utilizarse dentro de CuerpoProvider");
  return context;
}
